import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Edit, Plus, Trash2, Building2, Wallet, TrendingUp, Receipt, ArrowDownToLine, X } from 'lucide-react';

const SERVICE_TYPES = ['عمرة', 'حج', 'تذاكر', 'تأشيرات', 'فنادق', 'نقل', 'خدمات سياحية', 'أخرى'];
const PAYMENT_METHODS = [
  { v: 'cash', l: 'نقدي' },
  { v: 'bank_transfer', l: 'تحويل بنكي' },
  { v: 'cheque', l: 'شيك' },
  { v: 'other', l: 'أخرى' },
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

// 🆕 يحوّل رابط الوثيقة إلى عنوان قابل للعرض:
//  • إن كان base64 (data:image/...) → يستخدمه كما هو.
//  • إن كان مسار خادم (/uploads/...) → يضيف API base + token للمعاينة.
//  • إن كان رابط مطلق (http...) → يستخدمه كما هو.
const resolveDocUrl = (url) => {
  if (!url) return '';
  const s = String(url);
  if (s.startsWith('data:') || /^https?:\/\//i.test(s)) return s;
  const token = localStorage.getItem('token') || '';
  return `${API}${s.startsWith('/') ? s : `/${s}`}${token ? `?token=${token}` : ''}`;
};

// 🆕 مودال صغير لعرض وثيقة الإثبات (صورة) بحجم كبير + زر تحميل.
const DocumentPreviewModal = ({ url, title = 'وثيقة الإثبات', onClose }) => {
  if (!url) return null;
  const src = resolveDocUrl(url);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" dir="rtl" data-testid="doc-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">📎 {title}</DialogTitle>
        </DialogHeader>
        <div className="p-2 bg-gray-50 rounded border max-h-[70vh] overflow-auto text-center">
          <img src={src} alt={title} className="mx-auto max-w-full h-auto" />
        </div>
        <DialogFooter>
          <a
            href={src}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="doc-preview-download"
          >
            ⬇️ تحميل
          </a>
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SuppliersManagement = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = ['super_admin', 'general_accountant', 'general_manager'].includes(user?.role);

  const [tab, setTab] = useState('list');
  const [suppliers, setSuppliers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSuppliers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/suppliers`, { headers: authHeaders() });
      setSuppliers(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchSummary = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await axios.get(`${API}/suppliers/_stats/summary`, { headers: authHeaders() });
      setSummary(r.data);
    } catch (e) { console.error(e); }
  }, [isAdmin]);

  useEffect(() => { fetchSuppliers(); fetchSummary(); }, [fetchSuppliers, fetchSummary]);

  return (
    <div className="p-6 space-y-4" dir="rtl" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-yellow-700 flex items-center gap-2">
            <Building2 className="h-7 w-7" /> إدارة الموردين
          </h1>
          <p className="text-sm text-gray-500 mt-1">قائمة الموردين، خدماتهم، مستحقاتهم ودفعاتهم</p>
        </div>
      </div>

      {/* Summary cards (admins only) */}
      {isAdmin && summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card className="border-r-4 border-r-orange-500" data-testid="summary-open-dues">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">إجمالي المستحقات المفتوحة</div>
              <div className="text-2xl font-bold text-orange-700">{fmt(summary.total_dues_open)} <span className="text-sm">دج</span></div>
            </CardContent>
          </Card>
          <Card className="border-r-4 border-r-blue-500">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">المدفوع للموردين</div>
              <div className="text-2xl font-bold text-blue-700">{fmt(summary.total_paid)} <span className="text-sm">دج</span></div>
            </CardContent>
          </Card>
          <Card className="border-r-4 border-r-green-500" data-testid="summary-profit">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">الأرباح الإجمالية</div>
              <div className="text-2xl font-bold text-green-700">{fmt(summary.total_profit)} <span className="text-sm">دج</span></div>
            </CardContent>
          </Card>
          <Card className="border-r-4 border-r-yellow-500">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">عدد الموردين</div>
              <div className="text-2xl font-bold text-yellow-700">{summary.suppliers_count}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-list">قائمة الموردين</TabsTrigger>
          {isAdmin && <TabsTrigger value="dashboard" data-testid="tab-dashboard">لوحة التحكم</TabsTrigger>}
        </TabsList>

        <TabsContent value="list">
          <SuppliersList
            suppliers={suppliers}
            loading={loading}
            isAdmin={isAdmin}
            onRefresh={() => { fetchSuppliers(); fetchSummary(); }}
            onOpen={(s) => setSelected(s)}
          />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="dashboard">
            <DashboardTab summary={summary} suppliers={suppliers} />
          </TabsContent>
        )}
      </Tabs>

      {selected && (
        <SupplierDetailDialog
          supplier={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onRefresh={() => { fetchSuppliers(); fetchSummary(); }}
        />
      )}
    </div>
  );
};

// ============== List Tab ==============
const SuppliersList = ({ suppliers, loading, isAdmin, onRefresh, onOpen }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleEdit = (s) => { setEditing(s); setShowForm(true); };
  const handleNew = () => { setEditing(null); setShowForm(true); };

  return (
    <Card className="border-yellow-200">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">{loading ? 'جاري التحميل...' : `${suppliers.length} مورد`}</div>
          {isAdmin && (
            <Button onClick={handleNew} className="bg-yellow-600 hover:bg-yellow-700" data-testid="add-supplier-btn">
              <Plus className="h-4 w-4 ml-1" /> إضافة مورد
            </Button>
          )}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">الاسم</TableHead>
              <TableHead className="text-right">الهاتف</TableHead>
              <TableHead className="text-right">نوع الخدمات</TableHead>
              <TableHead className="text-right">العملة</TableHead>
              {isAdmin && <TableHead className="text-right">الرصيد المستحق</TableHead>}
              <TableHead className="text-right">الحالة</TableHead>
              <TableHead className="text-right">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suppliers.map((s) => (
              <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                <TableCell className="font-semibold">
                  <button className="text-yellow-700 hover:underline" onClick={() => onOpen(s)} data-testid={`open-supplier-${s.id}`}>
                    {s.name}
                  </button>
                </TableCell>
                <TableCell dir="ltr">{s.phone || '-'}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(s.service_types || []).map(t => (
                      <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>{s.currency}</TableCell>
                {isAdmin && (
                  <TableCell className={`font-mono ${(s.balance?.balance_due || 0) > 0 ? 'text-orange-700 font-bold' : 'text-gray-500'}`}>
                    {fmt(s.balance?.balance_due || 0)} دج
                  </TableCell>
                )}
                <TableCell>
                  <Badge variant={s.status === 'active' ? 'default' : 'secondary'} className={s.status === 'active' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-600'}>
                    {s.status === 'active' ? 'نشط' : 'متوقف'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(s)} data-testid={`edit-supplier-${s.id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && suppliers.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-8">لا يوجد موردون. ابدأ بإضافة مورد جديد.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>

        {showForm && (
          <SupplierFormDialog
            editing={editing}
            onClose={() => setShowForm(false)}
            onSaved={() => { setShowForm(false); onRefresh(); }}
          />
        )}
      </CardContent>
    </Card>
  );
};

// ============== Supplier Form ==============
const SupplierFormDialog = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', service_types: [], currency: 'DZD',
    payment_info: '', notes: '', status: 'active',
    ...(editing || {}),
  });
  const [saving, setSaving] = useState(false);

  const toggleType = (t) => {
    setForm((f) => ({
      ...f,
      service_types: f.service_types.includes(t)
        ? f.service_types.filter((x) => x !== t)
        : [...f.service_types, t],
    }));
  };

  const submit = async () => {
    if (!form.name.trim()) { alert('اسم المورد مطلوب'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/suppliers/${editing.id}`, form, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/suppliers`, form, { headers: authHeaders() });
      }
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || 'خطأ في الحفظ');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader>
          <DialogTitle>{editing ? 'تعديل مورد' : 'إضافة مورد جديد'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>الاسم *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" />
            </div>
            <div>
              <Label>الهاتف</Label>
              <Input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>البريد الإلكتروني</Label>
              <Input dir="ltr" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>العملة</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DZD">DZD (دينار جزائري)</SelectItem>
                  <SelectItem value="EUR">EUR (يورو)</SelectItem>
                  <SelectItem value="USD">USD (دولار)</SelectItem>
                  <SelectItem value="SAR">SAR (ريال سعودي)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>نوع الخدمات</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {SERVICE_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleType(t)}
                  className={`px-3 py-1 rounded-full text-sm border transition ${form.service_types.includes(t) ? 'bg-yellow-100 border-yellow-400 text-yellow-800 font-medium' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                  data-testid={`service-type-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>معلومات الدفع (RIB, عنوان البنك...)</Label>
            <Textarea rows={2} value={form.payment_info || ''} onChange={(e) => setForm({ ...form, payment_info: e.target.value })} />
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div>
            <Label>الحالة</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">نشط</SelectItem>
                <SelectItem value="inactive">متوقف</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} disabled={saving} className="bg-yellow-600 hover:bg-yellow-700" data-testid="save-supplier-btn">
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Supplier Detail Dialog (services + ledger + payments) ==============
const SupplierDetailDialog = ({ supplier, isAdmin, onClose, onRefresh }) => {
  const [services, setServices] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [agencyFilter, setAgencyFilter] = useState('all');
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState('services');

  // 📅 Date range filter — empty by default = all-time (existing behaviour).
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const reload = useCallback(async () => {
    try {
      const [svc] = await Promise.all([
        axios.get(`${API}/suppliers/${supplier.id}/services`, { headers: authHeaders() }),
      ]);
      setServices(svc.data);
      if (isAdmin) {
        const qp = new URLSearchParams();
        if (startDate) qp.set('start_date', `${startDate}T00:00:00`);
        if (endDate) qp.set('end_date', `${endDate}T23:59:59`);
        if (agencyFilter && agencyFilter !== 'all') qp.set('agency_id', agencyFilter);
        const qs = qp.toString() ? `?${qp.toString()}` : '';
        const [led, pay, ags] = await Promise.all([
          axios.get(`${API}/suppliers/${supplier.id}/ledger${qs}`, { headers: authHeaders() }),
          axios.get(`${API}/suppliers/${supplier.id}/payments${qs}`, { headers: authHeaders() }),
          axios.get(`${API}/suppliers/${supplier.id}/agencies`, { headers: authHeaders() }).catch(() => ({ data: [] })),
        ]);
        setLedger(led.data);
        setPayments(pay.data);
        setAgencyOptions(ags.data || []);
      }
    } catch (e) { console.error(e); }
  }, [supplier.id, isAdmin, startDate, endDate, agencyFilter]);

  useEffect(() => { reload(); }, [reload]);

  const printReport = () => {
    const qp = new URLSearchParams();
    if (startDate) qp.set('start_date', `${startDate}T00:00:00`);
    if (endDate) qp.set('end_date', `${endDate}T23:59:59`);
    const token = localStorage.getItem('token');
    // Open in a new tab; HTML response includes its own print button.
    const url = `${API}/suppliers/${supplier.id}/ledger/print?${qp.toString()}`;
    // Use fetch+blob to send auth header then open in new tab.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.text())
      .then(html => {
        const blob = new Blob([html], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      })
      .catch(e => alert('فشل فتح التقرير: ' + e.message));
  };

  // small helper for quick presets
  const setRange = (s, e) => { setStartDate(s); setEndDate(e); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-yellow-700" /> {supplier.name}
            <Button variant="ghost" size="sm" className="mr-auto" onClick={onClose}><X className="h-4 w-4" /></Button>
          </DialogTitle>
        </DialogHeader>

        {isAdmin && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex flex-wrap items-end gap-2 mb-3" data-testid="supplier-date-filter">
            <div>
              <Label className="text-xs">من تاريخ</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                     className="w-40" data-testid="supplier-filter-start" />
            </div>
            <div>
              <Label className="text-xs">إلى تاريخ</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                     className="w-40" data-testid="supplier-filter-end" />
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => setRange('', '')}
                      data-testid="supplier-range-all">كل الفترة</Button>
              <Button size="sm" variant="outline" onClick={() => {
                const t = new Date(); const m = String(t.getMonth() + 1).padStart(2, '0');
                setRange(`${t.getFullYear()}-${m}-01`, t.toISOString().slice(0, 10));
              }}>هذا الشهر</Button>
              <Button size="sm" variant="outline" onClick={() => {
                const t = new Date();
                setRange(`${t.getFullYear()}-01-01`, t.toISOString().slice(0, 10));
              }}>هذه السنة</Button>
            </div>
            {agencyOptions.length > 0 && (
              <div>
                <Label className="text-xs">🏢 فلتر بالوكالة</Label>
                <select
                  value={agencyFilter}
                  onChange={(e) => setAgencyFilter(e.target.value)}
                  className="block w-44 mt-1 text-xs px-2 py-1.5 border rounded-md bg-white"
                  data-testid="supplier-agency-filter"
                >
                  <option value="all">كل الوكالات ({agencyOptions.length})</option>
                  {agencyOptions.map((a) => (
                    <option key={a.id} value={a.id}>{a.name} ({a.transaction_count})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={reload} data-testid="supplier-apply-filter">🔄 تحديث</Button>
              <Button size="sm" onClick={printReport} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="supplier-print-report">
                🖨️ طباعة التقرير
              </Button>
            </div>
          </div>
        )}

        {isAdmin && ledger && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Card className="bg-orange-50 border-orange-200"><CardContent className="p-3"><div className="text-xs text-gray-600">المستحق</div><div className="text-xl font-bold text-orange-700">{fmt(ledger.summary?.balance_due)}</div></CardContent></Card>
            <Card className="bg-blue-50 border-blue-200"><CardContent className="p-3"><div className="text-xs text-gray-600">إجمالي المدين</div><div className="text-xl font-bold text-blue-700">{fmt(ledger.summary?.total_dues)}</div></CardContent></Card>
            <Card className="bg-green-50 border-green-200"><CardContent className="p-3"><div className="text-xs text-gray-600">المدفوع</div><div className="text-xl font-bold text-green-700">{fmt(ledger.summary?.total_paid)}</div></CardContent></Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="services">خدمات المورد</TabsTrigger>
            {isAdmin && <TabsTrigger value="ledger">كشف الحساب</TabsTrigger>}
            {isAdmin && <TabsTrigger value="payments">الدفعات</TabsTrigger>}
          </TabsList>

          <TabsContent value="services">
            <ServicesTab supplierId={supplier.id} services={services} isAdmin={isAdmin} onReload={reload} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="ledger">
              <LedgerTab ledger={ledger} />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="payments">
              <PaymentsTab
                supplierId={supplier.id}
                payments={payments}
                balanceDue={ledger?.summary?.balance_due || 0}
                onReload={() => { reload(); onRefresh(); }}
              />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// ---- Services Tab (Phase 1: unified — read-only view of services tagged
// with this supplier in the hierarchical catalog + the legacy per-supplier
// services. Adding/editing happens in "الخدمات الهرمية" page to avoid
// duplication, per user instructions.)
const ServicesTab = ({ supplierId, services, isAdmin, onReload }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [hServices, setHServices] = useState([]);

  // 🆕 Also pull hierarchical services tagged with this supplier
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/hierarchical-services?supplier_id=${supplierId}`, { headers: authHeaders() });
        setHServices(r.data || []);
      } catch { /* ignore */ }
    })();
  }, [supplierId, services]);

  const del = async (id) => {
    if (!window.confirm('حذف هذه الخدمة؟')) return;
    try {
      await axios.delete(`${API}/suppliers/${supplierId}/services/${id}`, { headers: authHeaders() });
      onReload();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };

  return (
    <div className="space-y-4">
      {/* Hierarchical services tagged to this supplier (single source of truth) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-bold text-sm text-yellow-800">📚 خدمات هذا المورد من الكتالوج الهرمي</h4>
          <span className="text-xs text-gray-500">للإضافة/التعديل، اذهب إلى "إدارة الخدمات الهرمية"</span>
        </div>
        {hServices.length === 0 ? (
          <p className="text-sm text-gray-500 italic">لا توجد خدمات في الكتالوج الهرمي مرتبطة بهذا المورد بعد.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">اسم الخدمة</TableHead>
                <TableHead className="text-right">النوع</TableHead>
                <TableHead className="text-right">تاريخ المغادرة</TableHead>
                {isAdmin && <TableHead className="text-right">تكلفة المورد</TableHead>}
                <TableHead className="text-right">الحالة</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {hServices.map(s => (
                <TableRow key={s.id}>
                  <TableCell className="font-semibold">{s.name}</TableCell>
                  <TableCell><Badge variant="outline">{s.service_type}</Badge></TableCell>
                  <TableCell className="text-xs">{s.departure_date || '-'}</TableCell>
                  {isAdmin && <TableCell className="font-mono text-orange-700">{s.supplier_cost ? fmt(s.supplier_cost) : '-'}</TableCell>}
                  <TableCell><Badge className={s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>{s.is_active ? 'نشطة' : 'معطّلة'}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Legacy supplier-services catalog (kept for backward compat, hidden if empty) */}
      {(services.length > 0 || isAdmin) && (
        <div className="pt-3 border-t">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-sm text-gray-700">📦 الكتالوج المباشر للمورد {services.length === 0 ? '(فارغ)' : ''}</h4>
            {isAdmin && (
              <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-gray-200 hover:bg-gray-300 text-gray-700" size="sm" variant="outline">
                <Plus className="h-3.5 w-3.5 ml-1" /> إضافة خدمة مباشرة
              </Button>
            )}
          </div>
          {services.length === 0 ? (
            <p className="text-xs text-gray-400 italic">يُفضّل استعمال الكتالوج الهرمي. هذا الجدول يبقى للتوافق القديم.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">الاسم</TableHead>
                  <TableHead className="text-right">النوع</TableHead>
                  {isAdmin && <TableHead className="text-right">التكلفة</TableHead>}
                  {isAdmin && <TableHead className="text-right">إجراءات</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-semibold">{s.name}</TableCell>
                    <TableCell><Badge variant="outline">{s.service_type}</Badge></TableCell>
                    {isAdmin && <TableCell className="font-mono text-orange-700">{fmt(s.cost_price)}</TableCell>}
                    {isAdmin && (
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => { setEditing(s); setShowForm(true); }}><Edit className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => del(s.id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {showForm && (
        <SupplierServiceFormDialog
          supplierId={supplierId}
          editing={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); onReload(); }}
        />
      )}
    </div>
  );
};

const SupplierServiceFormDialog = ({ supplierId, editing, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: '', service_type: 'تذاكر', cost_price: '', min_sell_price: '',
    suggested_sell_price: '', quantity: '', start_date: '', end_date: '',
    currency: 'DZD', notes: '', active: true,
    ...(editing || {}),
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || form.cost_price === '') { alert('الاسم والتكلفة مطلوبان'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_price: parseFloat(form.cost_price),
        min_sell_price: form.min_sell_price !== '' ? parseFloat(form.min_sell_price) : null,
        suggested_sell_price: form.suggested_sell_price !== '' ? parseFloat(form.suggested_sell_price) : null,
        quantity: form.quantity !== '' ? parseInt(form.quantity, 10) : null,
      };
      if (editing) {
        await axios.put(`${API}/suppliers/${supplierId}/services/${editing.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/suppliers/${supplierId}/services`, payload, { headers: authHeaders() });
      }
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || 'خطأ في الحفظ');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader><DialogTitle>{editing ? 'تعديل خدمة' : 'إضافة خدمة جديدة'}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>اسم الخدمة *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="svc-name-input" /></div>
            <div>
              <Label>نوع الخدمة</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>تكلفة المورد *</Label><Input type="number" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} data-testid="svc-cost-input" /></div>
            <div><Label>أقل سعر بيع</Label><Input type="number" value={form.min_sell_price} onChange={(e) => setForm({ ...form, min_sell_price: e.target.value })} /></div>
            <div><Label>السعر المقترح للبيع</Label><Input type="number" value={form.suggested_sell_price} onChange={(e) => setForm({ ...form, suggested_sell_price: e.target.value })} data-testid="svc-suggested-input" /></div>
            <div><Label>الكمية / المقاعد</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
            <div><Label>تاريخ البداية</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>تاريخ النهاية</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            <div>
              <Label>العملة</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DZD">DZD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="SAR">SAR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>ملاحظات</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} disabled={saving} className="bg-yellow-600 hover:bg-yellow-700" data-testid="save-svc-btn">{saving ? '...' : 'حفظ'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Ledger Tab ----
const LedgerTab = ({ ledger }) => {
  const [docUrl, setDocUrl] = useState(null); // 🆕 الوثيقة المعروضة حالياً
  if (!ledger) return <div className="p-4 text-gray-500">جاري التحميل...</div>;
  const labels = { sale_due: '🟠 مبيعات (مستحق)', payment_made: '🟢 دفعة (تخفيض)', adjustment: '✏️ تسوية يدوية' };
  const methodLabels = { cash: 'نقداً', bank_transfer: 'حوالة', check: 'شيك', card: 'بطاقة' };
  const ws = ledger.window_summary;
  const hasFilter = ledger.filters && (ledger.filters.start_date || ledger.filters.end_date);
  return (
    <div className="space-y-3">
      {hasFilter && ws && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="supplier-window-summary">
          <Card className="bg-blue-50 border-blue-200"><CardContent className="p-3"><div className="text-xs text-gray-600">مبيعات في الفترة</div><div className="text-lg font-bold text-blue-700">{fmt(ws.total_dues)}</div></CardContent></Card>
          <Card className="bg-green-50 border-green-200"><CardContent className="p-3"><div className="text-xs text-gray-600">المدفوع في الفترة</div><div className="text-lg font-bold text-green-700">{fmt(ws.total_paid)}</div></CardContent></Card>
          <Card className="bg-teal-50 border-teal-200"><CardContent className="p-3"><div className="text-xs text-gray-600">الربح في الفترة</div><div className="text-lg font-bold text-teal-700">{fmt(ws.total_profit)}</div></CardContent></Card>
          <Card className="bg-gray-50"><CardContent className="p-3"><div className="text-xs text-gray-600">عدد الحركات</div><div className="text-lg font-bold text-gray-700">{ws.items_in_window}</div></CardContent></Card>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right">التاريخ</TableHead>
            <TableHead className="text-right">النوع</TableHead>
            <TableHead className="text-right">المبلغ</TableHead>
            <TableHead className="text-right">الربح</TableHead>
            <TableHead className="text-right">الوكالة</TableHead>
            {/* 🆕 New columns requested by user */}
            <TableHead className="text-right">تاريخ إنشاء الوصل</TableHead>
            <TableHead className="text-right">اسم العميل</TableHead>
            <TableHead className="text-right">رقم الوصل / الوصولات المغطاة</TableHead>
            <TableHead className="text-right">المرجع / الوصل</TableHead>
            <TableHead className="text-right">الوصف</TableHead>
            <TableHead className="text-right">بواسطة</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ledger.items.map((l) => {
            const isSale = l.kind === 'sale_due';
            const isPayment = l.kind === 'payment_made';
            const sourceDate = l.source_op_created_at
              ? new Date(l.source_op_created_at).toLocaleDateString('fr-FR')
              : '—';
            return (
            <TableRow key={l.id}>
              <TableCell className="text-xs">{new Date(l.created_at).toLocaleString('ar-DZ')}</TableCell>
              <TableCell><Badge variant="outline">{labels[l.kind] || l.kind}</Badge></TableCell>
              <TableCell className="font-mono">{fmt(l.amount)}</TableCell>
              <TableCell className="font-mono text-green-700">{l.profit != null ? fmt(l.profit) : '-'}</TableCell>
              <TableCell className="text-xs">
                {l.agency_name ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 rounded text-[11px] border border-violet-200">
                    🏢 {l.agency_name}
                  </span>
                ) : <span className="text-gray-400">—</span>}
              </TableCell>
              {/* 🆕 تاريخ إنشاء الوصل */}
              <TableCell className="text-xs text-gray-700" data-testid={`ledger-source-date-${l.id}`}>
                {sourceDate}
              </TableCell>
              {/* 🆕 اسم العميل */}
              <TableCell className="text-xs" data-testid={`ledger-client-${l.id}`}>
                {isSale ? (
                  l.client_name ? (
                    <span className="font-semibold text-gray-800">👤 {l.client_name}</span>
                  ) : <span className="text-gray-400">—</span>
                ) : isPayment ? (
                  <span className="text-[11px] text-gray-500">
                    {l.covered_receipts && l.covered_receipts.length > 0
                      ? `${l.covered_receipts.length} عميل`
                      : '—'}
                  </span>
                ) : <span className="text-gray-400">—</span>}
              </TableCell>
              {/* 🆕 رقم الوصل / الوصولات المغطاة */}
              <TableCell className="text-xs" data-testid={`ledger-receipts-${l.id}`}>
                {isSale ? (
                  l.source_op_no ? (
                    <span className="font-mono inline-block px-2 py-0.5 bg-orange-50 text-orange-800 rounded border border-orange-200">
                      {l.source_op_no}
                    </span>
                  ) : <span className="text-gray-400">—</span>
                ) : isPayment ? (
                  l.covered_receipts && l.covered_receipts.length > 0 ? (
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {l.covered_receipts.slice(0, 6).map((c, idx) => (
                        <span
                          key={`${l.id}-cov-${idx}`}
                          title={`${c.client_name || ''} • ${fmt(c.amount_consumed)} دج`}
                          className="font-mono inline-block px-1.5 py-0.5 bg-green-50 text-green-800 rounded border border-green-200 text-[10px]"
                        >
                          {c.source_op_no || '—'}
                        </span>
                      ))}
                      {l.covered_receipts.length > 6 && (
                        <span className="text-[10px] text-gray-500">
                          +{l.covered_receipts.length - 6} أخرى
                        </span>
                      )}
                    </div>
                  ) : <span className="text-gray-400">—</span>
                ) : <span className="text-gray-400">—</span>}
              </TableCell>
              <TableCell className="text-xs">
                {l.payment_reference || l.payment_method ? (
                  <div className="flex flex-col gap-0.5">
                    {l.payment_reference && (
                      <span className="font-mono text-blue-700">#{l.payment_reference}</span>
                    )}
                    {l.payment_method && (
                      <span className="text-[10px] text-gray-500">{methodLabels[l.payment_method] || l.payment_method}</span>
                    )}
                    {l.document_image_url && (
                      <button
                        type="button"
                        onClick={() => setDocUrl(l.document_image_url)}
                        className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 underline text-[10px]"
                        data-testid={`ledger-doc-${l.id}`}
                      >📎 وثيقة</button>
                    )}
                  </div>
                ) : <span className="text-gray-400">—</span>}
              </TableCell>
              <TableCell>{l.description || '-'}</TableCell>
              <TableCell className="text-xs text-gray-500">{l.created_by_name || '-'}</TableCell>
            </TableRow>
          );})}
          {ledger.items.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-gray-500 py-6">لا توجد حركات في الفترة المحددة.</TableCell></TableRow>}
        </TableBody>
      </Table>
      {docUrl && <DocumentPreviewModal url={docUrl} onClose={() => setDocUrl(null)} />}
    </div>
  );
};

// ---- Payments Tab ----
const PaymentsTab = ({ supplierId, payments, balanceDue, onReload }) => {
  const [showForm, setShowForm] = useState(false);
  const [docUrl, setDocUrl] = useState(null); // 🆕 الوثيقة المعروضة حالياً
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">المستحق الحالي: <span className="font-bold text-orange-700">{fmt(balanceDue)} دج</span></div>
        <Button onClick={() => setShowForm(true)} className="bg-green-600 hover:bg-green-700" size="sm" disabled={balanceDue <= 0} data-testid="pay-supplier-btn">
          <ArrowDownToLine className="h-3.5 w-3.5 ml-1" /> دفع للمورد
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right">التاريخ</TableHead>
            <TableHead className="text-right">المبلغ</TableHead>
            <TableHead className="text-right">الطريقة</TableHead>
            <TableHead className="text-right">المرجع</TableHead>
            <TableHead className="text-right">الوثيقة</TableHead>
            <TableHead className="text-right">قبل</TableHead>
            <TableHead className="text-right">بعد</TableHead>
            <TableHead className="text-right">بواسطة</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="text-xs">{new Date(p.created_at).toLocaleString('ar-DZ')}</TableCell>
              <TableCell className="font-mono font-bold text-green-700">{fmt(p.amount)}</TableCell>
              <TableCell><Badge variant="outline">{PAYMENT_METHODS.find(x => x.v === p.method)?.l || p.method}</Badge></TableCell>
              <TableCell className="text-xs">{p.reference || '-'}</TableCell>
              <TableCell className="text-xs">
                {p.document_image_url ? (
                  <button
                    type="button"
                    onClick={() => setDocUrl(p.document_image_url)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded text-[11px] border border-emerald-200"
                    data-testid={`payment-doc-${p.id}`}
                  >📎 وثيقة</button>
                ) : (
                  <span className="text-[10px] text-red-500" title="لا توجد وثيقة (دفعة قديمة)">⚠️ بدون</span>
                )}
              </TableCell>
              <TableCell className="font-mono text-gray-600">{fmt(p.balance_before)}</TableCell>
              <TableCell className="font-mono text-orange-700">{fmt(p.balance_after)}</TableCell>
              <TableCell className="text-xs text-gray-500">{p.created_by_name || '-'}</TableCell>
            </TableRow>
          ))}
          {payments.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-6">لا توجد دفعات.</TableCell></TableRow>}
        </TableBody>
      </Table>

      {showForm && (
        <PayDialog supplierId={supplierId} balanceDue={balanceDue} onClose={() => setShowForm(false)} onPaid={() => { setShowForm(false); onReload(); }} />
      )}
      {docUrl && <DocumentPreviewModal url={docUrl} onClose={() => setDocUrl(null)} />}
    </div>
  );
};

const PayDialog = ({ supplierId, balanceDue, onClose, onPaid }) => {
  const [form, setForm] = useState({ amount: balanceDue, method: 'bank_transfer', reference: '', notes: '', payment_date: '' });
  const [docFile, setDocFile] = useState(null);
  const [docPreview, setDocPreview] = useState('');
  const [saving, setSaving] = useState(false);

  // 🆕 وثيقة الإثبات إلزامية — تحوّل الصورة إلى base64 data URL قبل الإرسال.
  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setDocFile(null); setDocPreview(''); return; }
    if (f.size > 2 * 1024 * 1024) { alert('حجم الملف يجب أن يكون أقل من 2MB'); return; }
    const r = new FileReader();
    r.onload = () => { setDocFile(f); setDocPreview(String(r.result || '')); };
    r.readAsDataURL(f);
  };

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { alert('المبلغ غير صالح'); return; }
    if (!docPreview) { alert('📷 يجب رفع صورة الوصل/الوثيقة قبل تأكيد الدفع.'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/suppliers/${supplierId}/payments`,
        { ...form, amount: amt, document_image_url: docPreview },
        { headers: authHeaders() }
      );
      onPaid();
    } catch (e) {
      alert(e.response?.data?.detail || 'خطأ');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader><DialogTitle>دفع للمورد</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-3 rounded-md bg-orange-50 border border-orange-200 text-sm">
            <span className="text-gray-700">المستحق الحالي: </span>
            <span className="font-bold text-orange-700">{fmt(balanceDue)} دج</span>
          </div>
          <div><Label>المبلغ *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount-input" /></div>
          <div>
            <Label>طريقة الدفع</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_METHODS.map(x => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>المرجع (شيك، رقم تحويل، ...)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
          <div><Label>تاريخ الدفع</Label><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} /></div>
          <div><Label>ملاحظات</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          {/* 🆕 وثيقة الإثبات — إلزامية */}
          <div className="p-3 rounded-md bg-amber-50 border-2 border-amber-300">
            <Label className="text-amber-800 font-semibold">📷 صورة الوصل / وثيقة الإثبات <span className="text-red-600">*</span></Label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={onPickFile}
              className="block w-full mt-1 text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-amber-600 file:text-white hover:file:bg-amber-700"
              data-testid="supplier-pay-doc-input"
            />
            <p className="text-xs text-amber-700 mt-1">JPG / PNG حتى 2MB — مطلوب لتوثيق الدفع في المحاسبة العامة.</p>
            {docPreview && (
              <div className="mt-2 border rounded p-1 bg-white">
                <img src={docPreview} alt="وثيقة" className="max-h-32 mx-auto" />
                <div className="text-center text-xs text-green-700 mt-1">✅ {docFile?.name}</div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} disabled={saving || !docPreview} className="bg-green-600 hover:bg-green-700" data-testid="confirm-pay-btn">{saving ? '...' : 'تأكيد الدفع'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Dashboard Tab ----
const DashboardTab = ({ summary, suppliers }) => {
  if (!summary) return <div className="p-4 text-gray-500">جاري التحميل...</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardContent className="p-4">
          <h3 className="font-bold text-yellow-700 mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4" /> أكبر الموردين بالمستحقات</h3>
          {(summary.top_creditors || []).length === 0 ? (
            <p className="text-sm text-gray-500">لا توجد مستحقات حالياً.</p>
          ) : (
            <ul className="space-y-2">
              {summary.top_creditors.map((s) => (
                <li key={s.id} className="flex justify-between items-center p-2 rounded bg-orange-50 border border-orange-100">
                  <span className="font-medium">{s.name}</span>
                  <span className="font-mono font-bold text-orange-700">{fmt(s.balance_due)} {s.currency}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <h3 className="font-bold text-yellow-700 mb-3 flex items-center gap-2"><Receipt className="h-4 w-4" /> ملخص محاسبي</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>إجمالي المبيعات للموردين:</span><span className="font-mono font-bold">{fmt(summary.total_dues_raw)} دج</span></div>
            <div className="flex justify-between"><span>المدفوع منها:</span><span className="font-mono text-green-700">{fmt(summary.total_paid)} دج</span></div>
            <div className="flex justify-between border-t pt-2 mt-2"><span className="font-bold">المتبقي مستحقاً:</span><span className="font-mono font-bold text-orange-700">{fmt(summary.total_dues_open)} دج</span></div>
            <div className="flex justify-between bg-green-50 p-2 rounded mt-2"><span className="font-bold flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> الأرباح الصافية:</span><span className="font-mono font-bold text-green-700">{fmt(summary.total_profit)} دج</span></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuppliersManagement;
